import { api } from './api'

/** Error code returned by API when clinic must complete Stripe Connect onboarding */
export const STRIPE_ONBOARDING_REQUIRED_CODE = 'STRIPE_ONBOARDING_REQUIRED'

/** User-friendly message when Stripe onboarding is required */
export const STRIPE_ONBOARDING_REQUIRED_MESSAGE =
  'Complete Stripe Connect onboarding in Payments to accept payments.'

/**
 * Returns true if the error indicates the clinic needs to complete Stripe onboarding.
 */
export function isStripeOnboardingRequiredError(error: unknown): boolean {
  const err = error as { code?: string; message?: string }
  return (
    err?.code === STRIPE_ONBOARDING_REQUIRED_CODE ||
    (typeof err?.message === 'string' &&
      (err.message.includes('Stripe Connect onboarding') ||
        err.message.includes('not fully onboarded') ||
        err.message.includes('payment account not active')))
  )
}

/**
 * User-facing copy when Stripe onboarding is required (for toasts / UI).
 */
export const STRIPE_ONBOARDING_TOAST = {
  title: 'Complete Stripe setup to accept payments',
  description:
    'Your clinic must finish Stripe Connect onboarding in Settings → Payments before processing payments.',
} as const

export interface PaymentRequest {
  prescription_id: string
  clinic_id: string
  amount: number
  payment_method_id?: string
  token?: string
  fraud_session_id?: string
  is_subscription?: boolean
  subscription_frequency?: 'weekly' | 'monthly' | 'quarterly'
  subscription_auto_renew?: boolean
  shipping?: {
    carrier_id: string
    carrier_name: string
    service_id: string
    service_name: string
    cost_cents: number
    original_cost_cents: number
    is_free_shipment: boolean
    free_shipment_type: string | null
  }
}

export interface PaymentResponse {
  success: boolean
  transaction?: {
    id: string
    status: string
    amount: number
    currency: string
    stripe_payment_intent_id?: string
  }
  error?: {
    message: string
    requires_action?: boolean
    client_secret?: string
  }
}

export async function processPayment(data: PaymentRequest): Promise<PaymentResponse> {
  const response = await api.post<PaymentResponse>('/payments/process', data)
  return response
}

export async function processRefund(
  transactionId: string,
  refundData: { amount?: number; reason: string }
): Promise<PaymentResponse> {
  const response = await api.post<PaymentResponse>(`/payments/${transactionId}/refund`, refundData)
  return response
}
